import React, { useState } from "react";
import { ASSETS } from "../lib/assets";
import SectionHeading from "../components/SectionHeading";
import usePageMeta from "../hooks/usePageMeta";
import { Calendar as CalendarIcon, Loader2, PartyPopper, Download } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { format } from "date-fns";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { BUSINESS } from "../lib/assets";
import MenuCardWithLightbox from "../components/MenuCardWithLightbox";
import { saveImageToPhotos } from "../lib/saveImage";

const EVENT_TYPES = ["Wedding", "Corporate", "Birthday", "Family Reunion", "Graduation", "Anniversary", "Church Event", "Fundraiser", "Other"];

const CATERING_SHOTS = [
  { key: "cateringSpread", label: "The Full Spread", note: "Chafing dishes, florals, service tools — plug-and-play setup for any venue." },
  { key: "cateringBrisket", label: "Competition Brisket", note: "Hand-sliced, salt-&-pepper crust, pink smoke ring." },
  { key: "cateringPulledPork", label: "Pulled Pork · Pans", note: "Slow-smoked and pulled on-site. Full pans for 30–40 guests." },
  { key: "cateringMac", label: "Mac & Cheese", note: "Sharp cheddar, cracker crust, cooked in full-size pans." },
  { key: "cateringPotatoSalad", label: "Potato Salad", note: "Family recipe, paprika-dusted, mustard-forward." },
  { key: "cateringSides", label: "Homestyle Sides", note: "Mashed, mac, potato salad, greens — served with our house sauces." },
  { key: "cateringElote", label: "Peach Cobbler Baked Beans", note: "Slow-simmered baked beans with a peach cobbler top — sweet, smoky, addictive." },
];

export default function Catering() {
  usePageMeta({
    title: "Catering & Weddings",
    description: "Full-service BBQ catering for weddings, corporate events, tailgates and family gatherings across Pasco, Hernando, Citrus, Northern Pinellas and Hillsborough counties. Get a custom quote.",
    path: "/catering",
  });
  const [date, setDate] = useState();
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ name: "", email: "", message: "" });
  const [savingCateringMenu, setSavingCateringMenu] = useState(false);

  const saveCateringMenu = async () => {
    setSavingCateringMenu(true);
    try {
      await saveImageToPhotos(
        ASSETS.menuCardCatering,
        "when-pigs-fly-catering-menu.jpg",
        "When Pigs Fly BBQ Catering Menu",
      );
    } catch (err) {
      if (!err || err.name !== "AbortError") toast.error("Couldn't save the catering menu.");
    } finally {
      setSavingCateringMenu(false);
    }
  };
  const [form, setForm] = useState({
    name: "", email: "", phone: "", best_time_to_reach: "",
    event_time: "", guest_count: 50, event_location: "", event_type: "Wedding",
    menu_preferences: "", additional_details: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Please select an event date."); return; }
    setLoading(true);
    try {
      const payload = { ...form, guest_count: Number(form.guest_count), event_date: format(date, "yyyy-MM-dd") };
      const { data } = await api.post("/catering", payload);
      setConfirmed(data);
      setShowConfirm(true);
      toast.success("Inquiry sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", best_time_to_reach: "", event_time: "", guest_count: 50, event_location: "", event_type: "Wedding", menu_preferences: "", additional_details: "" });
      setDate(undefined);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally { setLoading(false); }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    try {
      await api.post("/contact", {
        name: emailForm.name,
        email: emailForm.email,
        subject: "Catering inquiry",
        message: emailForm.message,
      });
      toast.success("Got it — we'll email you back within 24 hours.");
      setEmailForm({ name: "", email: "", message: "" });
      setShowEmail(false);
    } catch {
      toast.error("Couldn't send. Try again or use your email app.");
    } finally { setEmailLoading(false); }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink bg-ink text-bone" data-testid="catering-hero">
        <div className="absolute inset-0 opacity-45">
          <img src={ASSETS.cateringSpread} alt="" className="mono-img h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/50" />
        </div>
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-end gap-8 px-4 py-24 md:grid-cols-12 md:px-8 md:py-32">
          <div className="md:col-span-8">
            <div className="eyebrow mb-3 text-bone/60">Weddings · Corporate · Family · Events</div>
            <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">Book The Pit<br />For Your Event</h1>
            <p className="mt-6 max-w-xl font-serif text-base leading-relaxed text-bone/80">
              Looking to bring authentic BBQ flavor to your next event? Corporate gathering, wedding, family reunion,
              or milestone celebration — When Pigs Fly Family BBQ makes it unforgettable with slow-smoked meats and
              homestyle sides.
            </p>
          </div>
        </div>
      </section>

      {/* RECENT CATERING PHOTOS */}
      <section className="border-b border-ink bg-bone py-20 md:py-24" data-testid="catering-photos-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="Real events. Real pans." title="Recent Catering Setups" subtitle="Every event is set with chafing dishes, warmers, serving tools, and a uniformed crew — plated, buffet, or family style." />

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
            {/* Feature large */}
            <div className="grain-block relative aspect-[4/3] overflow-hidden border border-ink md:col-span-7">
              <img src={ASSETS[CATERING_SHOTS[0].key]} alt={CATERING_SHOTS[0].label} className="mono-img h-full w-full object-cover" />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-ink/75 p-4 font-mono text-[11px] uppercase tracking-[0.2em] text-bone">
                {CATERING_SHOTS[0].label} — {CATERING_SHOTS[0].note}
              </figcaption>
            </div>
            {/* Right column, 2 stacked images */}
            <div className="grid grid-cols-1 gap-6 md:col-span-5">
              {CATERING_SHOTS.slice(1, 3).map((s) => (
                <figure key={s.key} className="grain-block relative aspect-[16/10] overflow-hidden border border-ink">
                  <img src={ASSETS[s.key]} alt={s.label} className="mono-img h-full w-full object-cover" />
                  <figcaption className="absolute bottom-0 left-0 right-0 bg-ink/75 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
                    {s.label}
                  </figcaption>
                </figure>
              ))}
            </div>
            {/* Trio row */}
            {CATERING_SHOTS.slice(3, 6).map((s) => (
              <figure key={s.key} className="grain-block relative aspect-[4/3] overflow-hidden border border-ink md:col-span-4">
                <img src={ASSETS[s.key]} alt={s.label} className="mono-img h-full w-full object-cover" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-ink/75 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
                  {s.label}
                </figcaption>
              </figure>
            ))}
            {/* Bottom pair */}
            {CATERING_SHOTS.slice(6).map((s) => (
              <figure key={s.key} className="grain-block relative aspect-[4/3] overflow-hidden border border-ink md:col-span-6">
                <img src={ASSETS[s.key]} alt={s.label} className="mono-img h-full w-full object-cover" />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-ink/75 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
                  {s.label} · {s.note}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="border-b border-ink py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-5">
            <SectionHeading eyebrow="What we offer" title="Full-Service BBQ Catering" subtitle="Every detail handled — from the smoker to the serving line." />
          </div>
          <div className="md:col-span-7">
            <ul className="divide-y divide-ink/30 border-y border-ink/30">
              {[
                "Full-service BBQ catering for events of all sizes",
                "Slow-smoked meats — brisket, ribs, pulled pork & chicken",
                "Homemade sides and family fixings",
                "Flexible menu options to fit your needs and budget",
                "Professional setup and uniformed service crew",
                "Buffet, plated, or family-style service",
              ].map((f, i) => (
                <li key={f} className="grid grid-cols-12 items-center gap-4 py-4">
                  <div className="col-span-1 font-mono text-xs text-ink/60">0{i + 1}</div>
                  <div className="col-span-11 font-serif text-base">{f}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CATERING MENU CARD */}
      <section className="border-b border-ink bg-ink py-16 text-bone md:py-24" data-testid="catering-menu-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="eyebrow text-bone/60">Full menu</div>
              <h2 className="mt-2 font-rye text-3xl uppercase leading-none text-bone md:text-5xl">
                Catering Menu
              </h2>
              <p className="mt-3 max-w-2xl font-serif text-sm text-bone/70">
                Meats by the pound, homestyle sides, and a serving guide so you know how much to order per guest.
              </p>
            </div>
            <button
              type="button"
              onClick={saveCateringMenu}
              disabled={savingCateringMenu}
              data-testid="catering-menu-download-btn"
              className="inline-flex items-center gap-2 border-2 border-bone bg-transparent px-5 py-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink disabled:opacity-60"
            >
              {savingCateringMenu ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {savingCateringMenu ? "Saving…" : "Save to Photos"}
            </button>
          </div>
          <MenuCardWithLightbox
            src={ASSETS.menuCardCatering}
            alt="When Pigs Fly Family BBQ — Full Catering Menu (Meats, Homemade Sides, Serving Guide)"
            testid="catering-menu-card"
          />
        </div>
      </section>

      {/* EMAIL US CTA */}
      <section className="border-b border-ink bg-ink py-12 text-bone" data-testid="catering-email-cta">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-4 md:flex-row md:items-center md:px-8">
          <div>
            <div className="eyebrow mb-2 text-bone/60">Prefer to email?</div>
            <h3 className="font-alfa text-2xl uppercase leading-tight sm:text-3xl">Send us a quick note</h3>
            <p className="mt-2 max-w-xl font-serif text-sm text-bone/80">
              Not ready for the full quote form? Drop us a line and we&rsquo;ll follow up.
            </p>
          </div>
          <button
            type="button"
            data-testid="open-email-modal-btn"
            onClick={() => setShowEmail(true)}
            className="inline-flex items-center gap-2 border-2 border-bone bg-bone px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-colors hover:bg-transparent hover:text-bone"
          >
            <Mail size={16} /> Email Us
          </button>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section className="border-b border-ink py-20 md:py-28" data-testid="catering-form-section">
        <div className="mx-auto max-w-[1100px] px-4 md:px-8">
          <SectionHeading eyebrow="Request a quote" title="Tell Us About Your Event" subtitle="Fill out the form below and we'll get back to you within 24 hours with a custom quote." />

          <form onSubmit={submit} className="mt-12 grid grid-cols-1 gap-x-6 gap-y-8 border border-ink bg-bone p-6 shadow-hard md:grid-cols-2 md:p-10" data-testid="catering-form">
            {/* Event Date */}
            <div className="md:col-span-1">
              <label className="eyebrow mb-2 block text-ink/70">Event Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="catering-date-btn"
                    className="flex w-full items-center justify-between border border-ink bg-transparent px-3 py-3 text-left font-serif text-sm"
                  >
                    {date ? format(date, "PPP") : <span className="text-ink/50">Select event date</span>}
                    <CalendarIcon size={16} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto rounded-none border-ink bg-bone p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <Field label="Event Time" value={form.event_time} onChange={set("event_time")} placeholder="e.g. 5:00 PM" testid="catering-time" />
            <Field type="number" label="Number of Guests *" value={form.guest_count} onChange={set("guest_count")} required min="1" testid="catering-guests" />
            <Field label="Event Location *" value={form.event_location} onChange={set("event_location")} required placeholder="City, venue" testid="catering-location" />

            <div>
              <label className="eyebrow mb-2 block text-ink/70">Event Type *</label>
              <select
                required
                value={form.event_type}
                onChange={set("event_type")}
                data-testid="catering-event-type"
                className="w-full border border-ink bg-transparent px-3 py-3 font-serif text-sm focus:outline-none"
              >
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <Field label="Menu Preferences" value={form.menu_preferences} onChange={set("menu_preferences")} placeholder="Ribs, brisket, sides…" testid="catering-menu-prefs" />

            <div className="md:col-span-2">
              <label className="eyebrow mb-2 block text-ink/70">Additional Details</label>
              <textarea
                value={form.additional_details}
                onChange={set("additional_details")}
                rows={4}
                data-testid="catering-details"
                placeholder="Dietary restrictions, venue notes, service style, budget range…"
                className="w-full border border-ink bg-transparent px-3 py-3 font-serif text-sm focus:outline-none"
              />
            </div>

            <div className="md:col-span-2 my-2 h-px w-full bg-ink/20" />
            <div className="md:col-span-2">
              <div className="eyebrow mb-4 text-ink/60">Your contact information</div>
            </div>

            <Field label="Name *" value={form.name} onChange={set("name")} required testid="catering-name" />
            <Field label="Email *" type="email" value={form.email} onChange={set("email")} required testid="catering-email" />
            <Field label="Phone *" value={form.phone} onChange={set("phone")} required testid="catering-phone" />
            <Field label="Best Time to Reach You" value={form.best_time_to_reach} onChange={set("best_time_to_reach")} placeholder="Mornings, evenings, weekends…" testid="catering-best-time" />

            <div className="md:col-span-2 flex flex-col items-start gap-4 border-t border-ink pt-8 md:flex-row md:items-center md:justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">We look forward to serving you and making your event a success.</p>
              <button
                type="submit"
                disabled={loading}
                data-testid="catering-submit-btn"
                className="inline-flex items-center gap-3 border-2 border-ink bg-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink disabled:opacity-60"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <PartyPopper size={16} />} Send Inquiry
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Confirmation dialog — projected wedding date popup */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="rounded-none border-ink bg-bone shadow-hard" data-testid="catering-confirm-dialog">
          <DialogHeader>
            <div className="eyebrow mb-2 text-ink/60">Confirmed</div>
            <DialogTitle className="font-alfa text-3xl uppercase leading-none">You're On The Calendar</DialogTitle>
            <DialogDescription className="font-serif text-base text-ink/80">
              Your projected event date has been reserved as a tentative hold. We'll follow up within 24 hours to lock it in.
            </DialogDescription>
          </DialogHeader>
          {confirmed && (
            <div className="mt-4 border border-ink p-4 font-mono text-xs uppercase tracking-[0.14em]">
              <Row k="Event" v={confirmed.event_type} />
              <Row k="Projected Date" v={confirmed.event_date} />
              <Row k="Guests" v={String(confirmed.guest_count)} />
              <Row k="Location" v={confirmed.event_location} />
              <Row k="Ref #" v={confirmed.id.slice(0, 8)} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Us dialog */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="rounded-none border-ink bg-bone shadow-hard" data-testid="email-us-dialog">
          <DialogHeader>
            <div className="eyebrow mb-2 text-ink/60">Email the Pit</div>
            <DialogTitle className="font-alfa text-3xl uppercase leading-none">Send Us A Note</DialogTitle>
            <DialogDescription className="font-serif text-base text-ink/80">
              We&rsquo;ll follow up within 24 hours. This lands straight in our lead dashboard.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEmail} className="mt-4 space-y-4" data-testid="email-us-form">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">Your name</span>
              <input
                type="text"
                required
                value={emailForm.name}
                onChange={(e) => setEmailForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="email-us-name"
                className="mt-1 w-full border border-ink bg-white px-3 py-2 font-serif text-sm focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">Email</span>
              <input
                type="email"
                required
                value={emailForm.email}
                onChange={(e) => setEmailForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="email-us-email"
                className="mt-1 w-full border border-ink bg-white px-3 py-2 font-serif text-sm focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">Message</span>
              <textarea
                required
                rows={4}
                value={emailForm.message}
                onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))}
                data-testid="email-us-message"
                className="mt-1 w-full border border-ink bg-white px-3 py-2 font-serif text-sm focus:outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={emailLoading}
              data-testid="email-us-submit"
              className="inline-flex w-full items-center justify-center gap-2 border-2 border-ink bg-ink px-6 py-3 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors hover:bg-bone hover:text-ink disabled:opacity-60"
            >
              {emailLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {emailLoading ? "Sending…" : "Send message"}
            </button>
          </form>
          <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
            or{" "}
            <a
              href={`mailto:${BUSINESS.email}?subject=Catering%20inquiry`}
              data-testid="email-us-mailto"
              className="link-underline text-ink"
            >
              open your email app
            </a>
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-ink/20 py-2 last:border-b-0">
      <span className="text-ink/60">{k}</span>
      <span>{v}</span>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, type = "text", min, testid }) {
  return (
    <div>
      <label className="eyebrow mb-2 block text-ink/70">{label}</label>
      <input
        type={type}
        min={min}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full border border-ink bg-transparent px-3 py-3 font-serif text-sm focus:outline-none"
      />
    </div>
  );
}
